'use client';

import { AppPage } from '@/components/AppPage';
import { MyAccount } from '@/components/MyAccount/MyAccount';
import type { BuyerBusinessEntity } from '@wolfejs/ms-api-js/src/types/buyers';
import type { UserApiKeyEntity } from '@wolfejs/ms-api-js/src/types/users';

import { msJsClient } from '@/apiClients';
import type { MsApiErrorResponse } from '@wolfejs/ms-api-js/src/types/api';
import { ApiError } from '@wolfejs/ms-api-react/src/components/ApiError';
import { useMyAccount } from '@wolfejs/ms-api-react/src/hooks/myaccount/queries';
import { AdminPageHeader } from '@wolfejs/ms-ui/AdminPageHeader/AdminPageHeader';
import { Spinner } from '@wolfejs/ui/Spinner';
import { HttpStatusCode } from 'axios';
import React, { Suspense, useState } from 'react';

export const MyAccountContainer = () => {
  const [buyerData, setBuyerData] = useState<BuyerBusinessEntity>();
  const [buyerDataError, setBuyerDataError] = useState<MsApiErrorResponse>();
  const [apiKeyData, setApiKeyData] = useState<UserApiKeyEntity>();

  const { data: myAccount, error: userDetailsError } = useMyAccount();

  React.useEffect(() => {
    (async () => {
      if (!myAccount) return;

      const business = myAccount.buyer_business;
      const businessId = business?.id;

      if (!businessId) throw new Error('businessId not found in myaccount');

      const buyerData = myAccount ? await msJsClient.admin.buyer.getByBusinessId(businessId) : undefined;

      if (buyerData?.error) {
        if (buyerData?.error?.statusCode === HttpStatusCode.Unauthorized) {
          setBuyerDataError(buyerData?.error);
          return;
        }
      }
      setBuyerData(buyerData?.data);
      const apiKeyResponse = myAccount ? await msJsClient.auth.retrieveApiKey() : undefined;
      setApiKeyData(apiKeyResponse?.data);
    })();
  }, [myAccount]);

  return (
    <AppPage>
      <Suspense
        fallback={
          <div className="flex h-[65vh] items-center justify-center">
            <Spinner color="var(--primary)" size={50} />
          </div>
        }
      >
        <AdminPageHeader breadcrumbs={false} guideMe={false} pageName="User Settings" />
        {!myAccount ? (
          <div className="flex h-[65vh] items-center justify-center">
            <Spinner color="var(--primary)" size={50} />
          </div>
        ) : userDetailsError ? (
          <ApiError error={userDetailsError} />
        ) : buyerDataError ? (
          <ApiError error={'Failed to get business details'} />
        ) : (
          <MyAccount
            userDetails={myAccount}
            buyerData={buyerData}
            profile={myAccount?.profile}
            userApiKey={apiKeyData}
          />
        )}
      </Suspense>
    </AppPage>
  );
};
